import { useState, useEffect, useCallback, useRef } from 'react'
import * as d3 from 'd3'
import {
  clampPercent,
  snapPercentToStep,
  deepZoomPercent,
  percentToDeepZoomScale,
  fitZoomMaxScale,
  ZOOM_MIN_PERCENT,
  ZOOM_MAX_PERCENT_VECTOR,
} from '@/constants/zoom'
import { nextStepPercent } from '@/hooks/useZoom'
import { apiService } from '@/service/api'
import { PdfJsViewer } from '@/components/PdfViewer/PdfJsViewer'
import AdBlockerWarning from '@/components/AdBlockerWarning'
import { ZoomControls } from '@/components/intueri/ZoomControls'
import { Loader2 } from 'lucide-react'
import { useTranslation } from '@/i18n'

interface OriginalDocumentViewerProps {
  pnidId: string | undefined
  pageNumber?: number
}

export default function OriginalDocumentViewer({ pnidId, pageNumber }: OriginalDocumentViewerProps) {
  const { t } = useTranslation()
  const [documentUrl, setDocumentUrl] = useState<string | null>(null)
  const [fileType, setFileType] = useState<'pdf' | 'image'>('pdf')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(pageNumber || 1)
  const [hasCompatibilityIssues, setHasCompatibilityIssues] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null)
  const documentUrlRef = useRef<string | null>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null)
  const [zoomLevel, setZoomLevel] = useState(100)
  const initialScaleRef = useRef(1)

  useEffect(() => {
    let isCancelled = false

    const loadDocument = async () => {
      if (!pnidId) {
        setError(t('graph_editor:originalDocument.noDocument', { defaultValue: 'No P&ID document available' }))
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      try {
        // Download the P&ID image (not the document, which might be converted to PDF)
        const blob = await apiService.downloadPnidImage(pnidId, { type: 'original', page: pageNumber })

        // Check if this request is stale before updating state
        if (isCancelled) return

        // Revoke previous URL if it exists
        if (documentUrlRef.current) {
          URL.revokeObjectURL(documentUrlRef.current)
        }

        const url = URL.createObjectURL(blob)
        documentUrlRef.current = url

        // Detect file type from blob MIME type
        const detectedType: 'pdf' | 'image' = blob.type.startsWith('image/')
          ? 'image'
          : 'pdf'

        setDocumentUrl(url)
        setFileType(detectedType)
      } catch (err) {
        if (!isCancelled) {
          console.error('Error loading original P&ID document:', err)
          setError(t('graph_editor:originalDocument.loadFailed', { defaultValue: 'Failed to load original P&ID document' }))
        }
      } finally {
        if (!isCancelled) {
          setLoading(false)
        }
      }
    }

    loadDocument()

    return () => {
      isCancelled = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- pageNumber excluded intentionally: page navigation is handled client-side by PdfJsViewer
  }, [pnidId, retryCount])

  // Cleanup URL on component unmount
  useEffect(() => {
    return () => {
      if (documentUrlRef.current) {
        URL.revokeObjectURL(documentUrlRef.current)
        documentUrlRef.current = null
      }
    }
  }, [])

  // Sync page number from props
  useEffect(() => {
    if (pageNumber) {
      setCurrentPage(pageNumber)
    }
  }, [pageNumber])

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage)
  }

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1)
  }

  // Stable callback for onTotalPagesChange to prevent PDF reloading
  const handleTotalPagesChange = useCallback(() => {
    // No-op: We don't need to track total pages in this component
  }, [])

  // Load image dimensions when it's an image file
  useEffect(() => {
    if (fileType === 'image' && documentUrl) {
      const img = new Image()
      img.onload = () => {
        setImageSize({ width: img.width, height: img.height })
      }
      img.src = documentUrl
    }
  }, [fileType, documentUrl])

  // Setup D3 zoom behavior for images
  useEffect(() => {
    if (fileType !== 'image' || !svgRef.current || !imageSize) return

    const svg = d3.select(svgRef.current)
    const container = svgRef.current.parentElement
    if (!container) return

    const containerWidth = container.clientWidth
    const containerHeight = container.clientHeight

    // Calculate initial scale to fit image in container
    const scaleX = containerWidth / imageSize.width
    const scaleY = containerHeight / imageSize.height
    const initialScale = Math.min(scaleX, scaleY) * 0.9 // 90% to add some padding

    // Center the image
    const translateX = (containerWidth - imageSize.width * initialScale) / 2
    const translateY = (containerHeight - imageSize.height * initialScale) / 2

    // FS-562: deep-zoom map — 100% == the fit view (initialScale); zoom-out is
    // the uniform linear ratio down to ZOOM_MIN_PERCENT. The 200% cap reaches
    // `fitZoomMaxScale` (2× native pixels) so fine text on a large scan stays
    // readable — the previous 2× *fit* cap never reached native resolution for
    // big drawings (see `deepZoomPercent` in constants/zoom.ts).
    const minScale = initialScale * (ZOOM_MIN_PERCENT / 100)
    const maxScale = fitZoomMaxScale(initialScale)

    initialScaleRef.current = initialScale

    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([minScale, maxScale])
      .on('zoom', (event) => {
        svg.select('g').attr('transform', event.transform.toString())
        // Drive the readout from the actual transform so it tracks wheel zoom
        // too (not just button clicks). 100% == fit, 200% == max zoom.
        setZoomLevel(Math.round(deepZoomPercent(event.transform.k, initialScaleRef.current)))
      })

    svg.call(zoom)

    // Set initial transform (fires the zoom handler, which sets the readout to 100%)
    const initialTransform = d3.zoomIdentity
      .translate(translateX, translateY)
      .scale(initialScale)

    svg.call(zoom.transform, initialTransform)

    zoomRef.current = zoom

    return () => {
      svg.on('.zoom', null)
    }
  }, [fileType, imageSize])

  // One +/- click moves the readout by a clean 10pp: step in percent space
  // (snapping an off-grid wheel position toward the click direction, same as
  // useZoom surfaces), then map back to a transform scale. Above 100% the map
  // is geometric, so each click changes the scale by the same factor; scaleTo
  // clamps to the extent; the readout is driven by the zoom handler above.
  const stepZoom = useCallback((direction: 1 | -1) => {
    if (!svgRef.current || !zoomRef.current) return
    const currentPercent = deepZoomPercent(d3.zoomTransform(svgRef.current).k, initialScaleRef.current)
    const target = clampPercent(
      nextStepPercent(currentPercent, direction),
      ZOOM_MIN_PERCENT,
      ZOOM_MAX_PERCENT_VECTOR
    )
    const targetK = percentToDeepZoomScale(target, initialScaleRef.current)
    d3.select(svgRef.current).transition().duration(200).call(zoomRef.current.scaleTo, targetK)
  }, [])

  const handleZoomIn = useCallback(() => {
    stepZoom(1)
  }, [stepZoom])

  const handleZoomOut = useCallback(() => {
    stepZoom(-1)
  }, [stepZoom])

  // FS-562 type-in: snap to the 10% grid, clamp to the 200% vector cap, map back
  // to a transform scale (geometric above 100%). scaleTo also clamps to the d3
  // scaleExtent.
  const handleSetZoom = useCallback((percent: number) => {
    if (!svgRef.current || !zoomRef.current) return
    const target = clampPercent(snapPercentToStep(percent), ZOOM_MIN_PERCENT, ZOOM_MAX_PERCENT_VECTOR)
    const targetK = percentToDeepZoomScale(target, initialScaleRef.current)
    d3.select(svgRef.current).transition().duration(200).call(zoomRef.current.scaleTo, targetK)
  }, [])

  if (loading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400 dark:text-gray-500" />
        <span className="ml-2 text-gray-500 dark:text-dark-text-muted">{t('graph_editor:originalDocument.loading', { defaultValue: 'Loading original document...' })}</span>
      </div>
    )
  }

  if (error || !documentUrl) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-2">{error || t('graph_editor:originalDocument.loadFailedShort', { defaultValue: 'Failed to load document' })}</p>
          <button
            onClick={handleRetry}
            className="px-4 py-2 bg-gray-100 dark:bg-dark-accent border rounded hover:bg-gray-200 dark:hover:bg-dark-muted"
          >
            {t('graph_editor:originalDocument.retry', { defaultValue: 'Retry' })}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full w-full flex flex-col">
      {fileType === 'pdf' && (
        <AdBlockerWarning
          onDetectionComplete={setHasCompatibilityIssues}
          showToast={false}
          showInline={hasCompatibilityIssues}
        />
      )}
      <div className="flex-1 w-full min-h-0 relative">
        {fileType === 'pdf' ? (
          <PdfJsViewer
            url={documentUrl}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onTotalPagesChange={handleTotalPagesChange}
            initialRotation={0}
            initialZoom={0.7}
            initialAutoFit={true}
          />
        ) : (
          <>
            <svg
              ref={svgRef}
              className="h-full w-full bg-gray-50 dark:bg-dark-muted"
              style={{ cursor: 'grab' }}
              role="img"
              aria-label={t('graph_editor:originalDocument.imageAria', { defaultValue: 'Original P&ID document' })}
              onMouseDown={(e) => {
                if (e.currentTarget) {
                  e.currentTarget.style.cursor = 'grabbing'
                }
              }}
              onMouseUp={(e) => {
                if (e.currentTarget) {
                  e.currentTarget.style.cursor = 'grab'
                }
              }}
            >
              <g>
                <image
                  href={documentUrl}
                  width={imageSize?.width || 0}
                  height={imageSize?.height || 0}
                  preserveAspectRatio="xMidYMid meet"
                />
              </g>
            </svg>
            <ZoomControls
              placement="bottom-left"
              percent={zoomLevel}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onSetPercent={handleSetZoom}
              canZoomIn={zoomLevel < ZOOM_MAX_PERCENT_VECTOR}
              canZoomOut={zoomLevel > ZOOM_MIN_PERCENT}
            />
          </>
        )}
      </div>
    </div>
  )
}

